import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../hooks/redux';
import { loginUser, clearError, logoutUser, setMongoUser } from '../store/authSlice';
import { auth } from '../config/firebase';
import { checkAdminRole } from '../services/userService';
import { sendPasswordResetEmail } from 'firebase/auth';
import {appConfig} from '../config'
import logo from '../assets/logo.png'
import { toDisplayVersion } from '../utils/displayVersion';
import { peekReturnTo } from '../utils/returnTo';

const Login: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { loading, error, isAuthenticated, user } = useAppSelector((state) => state.auth);
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetMessage, setResetMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  useEffect(() => {
    if (isAuthenticated && user) {
      // Check if user has admin role and get MongoDB user data
      checkAdminRole().then(({ isAdmin, user: mongoUserData }) => {
        console.log('----------------- user is there', user.uid, 'isAdmin:', isAdmin);

        if (!isAdmin) {
          console.error('[Login] User does not have admin role:', user.uid);
          dispatch(logoutUser());
          dispatch(clearError());
          // Set error message after logout
          setTimeout(() => {
            dispatch({
              type: 'auth/setError',
              payload: 'Access denied. Your account does not have admin privileges to access this portal.'
            });
          }, 100);
        } else {
          // Store MongoDB user data in Redux
          console.log('he ------------', mongoUserData?.role)
          if (mongoUserData) {
            dispatch(setMongoUser(mongoUserData));
            console.log('[Login] ✅ MongoDB user data stored:', mongoUserData.email);
          }
          const isSuperAdmin = mongoUserData?.role === 'super_admin';
          // A deep link the user was bounced from wins over the role's home
          // route — they asked for a specific page (often from an invite
          // mail, query string and all), and that intent survives the
          // detour through sign-in. Falls back to the role home when there
          // is nothing remembered. ProtectedRoute clears it on arrival.
          navigate(peekReturnTo() || (isSuperAdmin ? '/knowledge-base' : '/dashboard'));
        }
      }).catch(error => {
        console.error('[Login] Error checking admin role:', error);
        dispatch(logoutUser());
        dispatch(clearError());
        setTimeout(() => {
          dispatch({
            type: 'auth/setError',
            payload: 'Failed to verify admin privileges. Please try again.'
          });
        }, 100);
      });
    }
  }, [isAuthenticated, user, navigate, dispatch]);

  useEffect(() => {
    dispatch(clearError());
  }, [dispatch]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    if (error) {
      dispatch(clearError());
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.email || !formData.password) {
      return;
    }
    dispatch(loginUser(formData));
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetEmail) {
      setResetMessage({ type: 'error', text: 'Please enter your email address' });
      return;
    }

    setResetLoading(true);
    setResetMessage(null);

    try {
      await sendPasswordResetEmail(auth, resetEmail);
      setResetMessage({
        type: 'success',
        text: 'If the email exists in our system, we have sent a password reset link.'
      });
      setResetEmail('');
    } catch (error: any) {
      console.error('Password reset error:', error);

      // For security, don't reveal if email exists or not
      // Only show specific errors for invalid format or rate limiting
      if (error.code === 'auth/invalid-email') {
        setResetMessage({ type: 'error', text: 'Please enter a valid email address' });
      } else if (error.code === 'auth/too-many-requests') {
        setResetMessage({ type: 'error', text: 'Too many requests. Please try again later' });
      } else {
        // For user-not-found and other errors, show generic success message
        // This prevents email enumeration attacks
        setResetMessage({
          type: 'success',
          text: 'If the email exists in our system, we have sent a password reset link.'
        });
        setResetEmail('');
      }
    } finally {
      setResetLoading(false);
    }
  };

  if (showResetPassword) {
    return (
      <Container maxWidth="sm" className="min-h-screen flex items-center justify-center">
        <Paper elevation={3} className="p-8 w-full">
          <Box className="text-center mb-6">
            <img
              src={logo}
              alt="Apexia Labs"
              style={{ maxWidth: '150px', height: 'auto', marginBottom: '16px', display: 'block', marginLeft: 'auto', marginRight: 'auto' }}
            />
            <Typography variant="h4" component="h1" gutterBottom>
              Reset Password
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Enter your email address and we'll send you a link to reset your password.
            </Typography>
          </Box>

          {resetMessage && (
            <Alert severity={resetMessage.type} className="mb-4">
              {resetMessage.text}
            </Alert>
          )}

          <Box component="form" onSubmit={handlePasswordReset}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={resetEmail}
              onChange={(e) => setResetEmail(e.target.value)}
              required
              margin="normal"
              autoComplete="email"
              autoFocus
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              className="mt-6 mb-4"
              disabled={resetLoading || !resetEmail}
              size="large"
            >
              {resetLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Send Reset Email'
              )}
            </Button>

            <Box className="text-center">
              <Button
                onClick={() => {
                  setShowResetPassword(false);
                  setResetMessage(null);
                  setResetEmail('');
                }}
                className="text-signature hover:text-signature-strong"
              >
                Back to Sign In
              </Button>
            </Box>
          </Box>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" className="min-h-screen flex items-center justify-center">
      <Paper elevation={3} className="p-8 w-full">
        <Box className="text-center mb-6">
          <img
            src={logo}
            alt="Apexia Labs"
            style={{ maxWidth: '250px', height: 'auto', marginBottom: '16px', display: 'block', marginLeft: 'auto', marginRight: 'auto' }}
          />
          <Typography variant="h4" component="h1" gutterBottom>
            {appConfig.name}
          </Typography>
          <Typography variant="h5" component="h2" color="textSecondary">
            Sign In
          </Typography>
          <Typography
            variant="caption"
            color="textSecondary"
            component="div"
            sx={{ mt: 1 }}
            title={`build ${appConfig.version}`}
          >
            v{toDisplayVersion(appConfig.version)}
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" className="mb-4">
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Email"
            type="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            required
            margin="normal"
            autoComplete="email"
            autoFocus
          />
          
          <TextField
            fullWidth
            label="Password"
            type={showPassword ? 'text' : 'password'}
            name="password"
            value={formData.password}
            onChange={handleInputChange}
            required
            margin="normal"
            autoComplete="current-password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    onClick={() => setShowPassword(!showPassword)}
                    onMouseDown={(e) => e.preventDefault()}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            className="mt-6 mb-4"
            disabled={loading || !formData.email || !formData.password}
            size="large"
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              'Sign In'
            )}
          </Button>

          <Box className="text-center">
            <Button
              onClick={() => {
                setShowResetPassword(true);
                setResetEmail(formData.email);
              }}
              className="text-signature hover:text-signature-strong mb-2"
              fullWidth
            >
              Forgot Password?
            </Button>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default Login;